type SentryEventFrame = {
  abs_path?: string;
  filename?: string;
  function?: string;
  module?: string;
};

type SentryExceptionValue = {
  type?: string;
  value?: string;
  stacktrace?: {
    frames?: SentryEventFrame[];
  };
};

export type SentryEventWithException = {
  exception?: {
    values?: SentryExceptionValue[];
  };
};

// Exact third-party source from CLIENTHUB-FRONTEND-457. Keep the match narrow
// so Client Hub's own failed requests continue to be reported.
const INJECTED_FETCH_SCRIPT =
  "chrome-extension://bkkbcggnhapdmkeljlodobbkopceiche/injectScriptAdjust.js";
const PROFITWELL_SCRIPT_PATH = "/js/profitwell.js";
const PROFITWELL_SCRIPT_URL = "https://public.profitwell.com/js/profitwell.js";

function isInjectedEmbedderPostMessageError(
  event: SentryEventWithException,
): boolean {
  return (event.exception?.values ?? []).some((exception) => {
    const exceptionMessage = exception.value ?? "";
    const isNullPostMessageError =
      exception.type === "TypeError" &&
      exceptionMessage.includes("Cannot read properties of null") &&
      exceptionMessage.includes("postMessage");
    const hasInjectedEmbedderFrame = (exception.stacktrace?.frames ?? []).some(
      (frame) => {
        return (
          frame.filename?.includes("apps/embedded/index.js") ||
          frame.module === "apps/embedded/index" ||
          frame.function === "dispatchEmbedderEvent"
        );
      },
    );

    return isNullPostMessageError && hasInjectedEmbedderFrame;
  });
}

function isInjectedBrowserExtensionFetchError(
  event: SentryEventWithException,
): boolean {
  return (event.exception?.values ?? []).some((exception) => {
    const isFetchError =
      exception.type === "TypeError" && exception.value === "Failed to fetch";
    const hasInjectedExtensionFrame = (exception.stacktrace?.frames ?? []).some(
      (frame) => frame.filename === INJECTED_FETCH_SCRIPT,
    );

    return isFetchError && hasInjectedExtensionFrame;
  });
}

function isProfitWellFetchError(event: SentryEventWithException): boolean {
  return (event.exception?.values ?? []).some((exception) => {
    const isFetchError =
      exception.type === "TypeError" && exception.value === "Failed to fetch";
    const hasProfitWellFrame = (exception.stacktrace?.frames ?? []).some(
      (frame) => {
        return [frame.filename, frame.abs_path].some((source) => {
          return (
            source === PROFITWELL_SCRIPT_PATH ||
            source === PROFITWELL_SCRIPT_URL ||
            source?.startsWith(`${PROFITWELL_SCRIPT_URL}?`)
          );
        });
      },
    );

    return isFetchError && hasProfitWellFrame;
  });
}

export function shouldDropSentryEvent(
  event: SentryEventWithException,
): boolean {
  return (
    isInjectedEmbedderPostMessageError(event) ||
    isInjectedBrowserExtensionFetchError(event) ||
    isProfitWellFetchError(event)
  );
}
